/**
 * Pre-lead redirect params that vary by the visitor's Everflow affiliate.
 *
 * Some partners (PEC for AIU/CTU) want static source labels on the clickout
 * URL that depend on where the visitor came from, e.g. affiliate 139 (Meta)
 * -> `subsrc1=Facebook&subsrc2=Social&webinit=<creative link>`. The map is
 * keyed by the Everflow affiliate ID captured on landing (`affid`, see
 * everflowAffiliateParams.ts); each entry lists literal query params to set.
 *
 * `getUrls().PreLeadRedirect` sets the matching entry's params on top of the
 * base URL's own query string, so an unmapped or missing affiliate adds
 * nothing and the base URL carries the defaults. Values are URL-encoded by the
 * builder like any other query value.
 */
import type { PreLeadRedirectCompositeParam } from "./preLeadRedirectComposite";

export type PreLeadRedirectAffiliateParams = Record<string, Record<string, string>>;

/**
 * Default query names for the Dreambound IDs the builder appends; a program's
 * `preLeadRedirectParams` renames them.
 */
export const DEFAULT_PRE_LEAD_REDIRECT_PARAM_NAMES = {
  marketplaceEventId: "m",
  customerId: "c",
  everflowTransactionId: "e",
  programSlug: "s",
  userZip: "z",
} as const;

type PreLeadRedirectIdKey = keyof typeof DEFAULT_PRE_LEAD_REDIRECT_PARAM_NAMES;

/**
 * Query names the builder emits itself: the default ID names, any renames, and
 * composite param names. Affiliate params must not use them, or they would
 * silently replace the IDs partners post back for attribution. The defaults
 * stay reserved even when renamed; nothing legitimate needs a param called `c`.
 */
export const getReservedPreLeadRedirectParamNames = ({
  preLeadRedirectParams,
  preLeadRedirectCompositeParams,
}: {
  preLeadRedirectParams?: Partial<Record<PreLeadRedirectIdKey, string | null>> | null;
  preLeadRedirectCompositeParams?: ReadonlyArray<PreLeadRedirectCompositeParam> | null;
}): Set<string> => {
  const reserved = new Set<string>(Object.values(DEFAULT_PRE_LEAD_REDIRECT_PARAM_NAMES));
  for (const name of Object.values(preLeadRedirectParams ?? {})) {
    if (name) reserved.add(name);
  }
  for (const composite of preLeadRedirectCompositeParams ?? []) {
    reserved.add(composite.paramName);
  }
  return reserved;
};

const AFFILIATE_ID_PATTERN = /^\d+$/;
const PARAM_NAME_PATTERN = /^[A-Za-z0-9_.-]+$/;

/**
 * Validate raw affiliate-param config (admin JSON field or GraphQL input).
 * Returns human-readable errors; an empty list means valid. `null`/`undefined`
 * is valid (no map). Pass the program's reserved names (see
 * getReservedPreLeadRedirectParamNames) to reject entries that would collide
 * with an ID or composite param.
 */
export const validatePreLeadRedirectAffiliateParams = (
  value: unknown,
  reservedNames: ReadonlySet<string> = new Set()
): string[] => {
  if (value == null) return [];
  if (typeof value !== "object" || Array.isArray(value)) {
    return ["preLeadRedirectAffiliateParams must be an object keyed by Everflow affiliate id."];
  }

  const errors: string[] = [];
  for (const [affiliateId, params] of Object.entries(value as Record<string, unknown>)) {
    if (affiliateId.trim() === "") {
      errors.push("affiliate id must not be empty.");
      continue;
    }
    if (!AFFILIATE_ID_PATTERN.test(affiliateId)) {
      errors.push(`affiliate id "${affiliateId}" must be numeric (the Everflow affiliate id).`);
      continue;
    }
    if (typeof params !== "object" || params === null || Array.isArray(params)) {
      errors.push(`affiliate "${affiliateId}" must be an object of param names to values.`);
      continue;
    }
    for (const [name, paramValue] of Object.entries(params)) {
      if (!PARAM_NAME_PATTERN.test(name)) {
        errors.push(
          `affiliate "${affiliateId}": param name "${name}" must match ${PARAM_NAME_PATTERN.source}.`
        );
      } else if (reservedNames.has(name)) {
        errors.push(
          `affiliate "${affiliateId}": "${name}" is reserved for a Dreambound ID or composite param; use another name.`
        );
      }
      if (typeof paramValue !== "string") {
        errors.push(`affiliate "${affiliateId}": "${name}" must be a string.`);
      }
    }
  }
  return errors;
};
